import * as Cesium from "cesium";

import { openOverlayTextEditorUI } from "./OverlayText"; // path adjust

// JS — openOverlayTextEditor: zoom to polygon, open UI, update state + entity
export function openOverlayTextEditor({ viewer, dataSource, entity, feature, stateRef, setMapObjects, originalCameraControls }) {
  // English: fly camera to feature polygon/bbox
  const flyToFeaturePolygon = (viewer, feature, duration = 0.8) => {
    const p = feature?.properties || {};
    let west, south, east, north;

    if (Array.isArray(p.bbox) && p.bbox.length === 4) {
      [west, south, east, north] = p.bbox.map(Number);
    } else if (feature?.geometry?.type === "Polygon") {
      const ring = feature.geometry.coordinates?.[0];
      if (Array.isArray(ring) && ring.length) {
        west = east = Number(ring[0][0]);
        south = north = Number(ring[0][1]);
        for (let i = 1; i < ring.length; i++) {
          const lon = Number(ring[i][0]);
          const lat = Number(ring[i][1]);
          if (lon < west) west = lon;
          if (lon > east) east = lon;
          if (lat < south) south = lat;
          if (lat > north) north = lat;
        }
      }
    }

    if ([west, south, east, north].some((v) => !Number.isFinite(v)) || east === west || north === south) return;

    const padLon = Math.max(0.0005, (east - west) * 0.06);
    const padLat = Math.max(0.0005, (north - south) * 0.06);
    const rect = Cesium.Rectangle.fromDegrees(west - padLon, south - padLat, east + padLon, north + padLat);
    viewer.camera.flyTo({ destination: rect, duration });
  };

  // English: draw text to canvas for billboard
  const makeTextCanvas = (text, px, fill = "#ffffff", outline = "rgba(0,0,0,0.9)", outlineWidth = 3) => {
    const dpr = window.devicePixelRatio || 1;
    const pad = Math.max(4, Math.ceil(px * 0.3));
    const tmp = document.createElement("canvas");
    const tctx = tmp.getContext("2d");
    tctx.font = `${px}px sans-serif`;
    const m = tctx.measureText(text || "");
    const baseH = Math.ceil(px * 1.2);
    const baseW = Math.ceil((m.actualBoundingBoxRight || m.width) - (m.actualBoundingBoxLeft || 0));
    const w = Math.max(1, baseW + pad * 2);
    const h = Math.max(1, baseH + pad * 2);

    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(w * dpr);
    canvas.height = Math.ceil(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;

    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.font = `${px}px sans-serif`;
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";

    const cx = w / 2,
      cy = h / 2;
    ctx.shadowColor = "rgba(0,0,0,0.25)";
    ctx.shadowBlur = Math.max(0, Math.floor(px * 0.15));
    if (outlineWidth > 0) {
      ctx.lineWidth = outlineWidth;
      ctx.strokeStyle = outline;
      ctx.strokeText(text || "", cx, cy);
    }
    ctx.fillStyle = fill;
    ctx.fillText(text || "", cx, cy);
    return canvas;
  };

  /* --- 0) focus camera on polygon --- */
  flyToFeaturePolygon(viewer, feature, 0.8);

  /* --- 1) open editor UI --- */
  openOverlayTextEditorUI(
    viewer,
    feature,
    (edited) => {
      // keep latest feature on entity
      entity._otFeature = edited;

      // update React state by id
      setMapObjects((prev) => {
        if (!Array.isArray(prev)) return prev;

        const next = [...prev];

        const idx = next.findIndex((o) =>
          o?.file_name
            ?.toLowerCase()
            .replace(/[\s\-_]/g, "")
            .includes("overlaytext")
        );
        if (idx === -1) return prev;

        const ogj = next[idx].original_geojson || {};
        const features = Array.isArray(ogj.features) ? [...ogj.features] : [];

        const editedId = edited?.properties?.id;

        if (!editedId) return prev;

        const fi = features.findIndex((f) => f?.properties?.id === editedId);
        if (fi >= 0) {
          features[fi] = edited;
        } else {
          features.push(edited);
        }

        next[idx] = {
          ...next[idx],
          original_geojson: { ...ogj, features },
          is_edited: true,
        };

        return next;
      });

      // refresh billboard texture and rotation
      const p = edited.properties || {};
      const text = String(p.text || "");
      const baseSize = Number.isFinite(+p.size) ? Math.max(1, +p.size) : 12;
      const minZoom = Number.isFinite(+p.zoom) ? +p.zoom : 0;
      const rotRad = Cesium.Math.toRadians(Number.isFinite(+p.rotationDeg) ? +p.rotationDeg : 0);

      const img = makeTextCanvas(text, baseSize, "#ffffff", "#000000", 3);
      entity.billboard.image = img;
      entity.billboard.rotation = rotRad;

      // move position if center changed
      if (p.center && Number.isFinite(p.center.lon) && Number.isFinite(p.center.lat)) {
        entity.position = Cesium.Cartesian3.fromDegrees(p.center.lon, p.center.lat, 0);
      }

      // update zoom logic state
      stateRef.baseSize = baseSize;
      stateRef.minZoom = minZoom;

      viewer.scene.requestRender();
    },
    originalCameraControls
  );
}

// OverlayText loader: labels only, zoom-threshold + size growth, and zoom-to-extent on load.
export async function loadOverlayTextGeoJSONToCesium({ viewer, geojson, options = {}, callback, layerName, id, invisible = false, setMapObjects, originalCameraControls }) {
  // --- helpers ---
  const toRadians = (deg) => (deg * Math.PI) / 180;

  const featureCenterLL = (feat) => {
    const p = feat.properties || {};
    if (p.center && Number.isFinite(p.center.lon) && Number.isFinite(p.center.lat)) return [p.center.lon, p.center.lat];
    if (Array.isArray(p.bbox) && p.bbox.length === 4) {
      const [w, s, e, n] = p.bbox;
      return [(w + e) / 2, (s + n) / 2];
    }
    const g = feat.geometry || {};
    if (g.type === "Point" && Array.isArray(g.coordinates)) return [g.coordinates[0], g.coordinates[1]];
    if (g.type === "Polygon" && Array.isArray(g.coordinates) && g.coordinates[0]?.length) {
      const ring = g.coordinates[0];
      let A = 0,
        cx = 0,
        cy = 0;
      for (let i = 0, n = ring.length - 1; i < n; i++) {
        const [x1, y1] = ring[i],
          [x2, y2] = ring[i + 1];
        const f = x1 * y2 - x2 * y1;
        A += f;
        cx += (x1 + x2) * f;
        cy += (y1 + y2) * f;
      }
      A = A || 1;
      return [cx / (3 * A), cy / (3 * A)];
    }
    return [0, 0];
  };

  // bounds utils
  const newEmptyBounds = () => ({ w: Infinity, s: Infinity, e: -Infinity, n: -Infinity });
  const addCoord = (b, lon, lat) => {
    b.w = Math.min(b.w, lon);
    b.s = Math.min(b.s, lat);
    b.e = Math.max(b.e, lon);
    b.n = Math.max(b.n, lat);
  };
  const scanCoords = (b, node) => {
    if (!node) return;
    if (Array.isArray(node)) {
      if (typeof node[0] === "number" && node.length >= 2) addCoord(b, node[0], node[1]);
      else for (const c of node) scanCoords(b, c);
    }
  };
  const featureBounds = (feat) => {
    const p = feat.properties || {};
    if (Array.isArray(p.bbox) && p.bbox.length === 4) {
      const [w, s, e, n] = p.bbox;
      return { w, s, e, n };
    }
    const g = feat.geometry;
    if (g && g.coordinates) {
      const b = newEmptyBounds();
      scanCoords(b, g.coordinates);
      if (Number.isFinite(b.w) && Number.isFinite(b.s) && Number.isFinite(b.e) && Number.isFinite(b.n)) return b;
    }
    // fallback: small box around center
    const [lon, lat] = featureCenterLL(feat);
    const pad = 0.001;
    return { w: lon - pad, s: lat - pad, e: lon + pad, n: lat + pad };
  };

  const makeTextCanvas = (text, px, fill = "#ffffff", outline = "rgba(0,0,0,0.9)", outlineWidth = 3) => {
    const dpr = window.devicePixelRatio || 1;
    const pad = Math.max(4, Math.ceil(px * 0.3));
    const tmp = document.createElement("canvas");
    const tctx = tmp.getContext("2d");
    tctx.font = `${px}px sans-serif`;
    const m = tctx.measureText(text || "");
    const baseH = Math.ceil(px * 1.2);
    const baseW = Math.ceil((m.actualBoundingBoxRight || m.width) - (m.actualBoundingBoxLeft || 0));
    const w = Math.max(1, baseW + pad * 2);
    const h = Math.max(1, baseH + pad * 2);

    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(w * dpr);
    canvas.height = Math.ceil(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;

    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.font = `${px}px sans-serif`;
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";

    const cx = w / 2,
      cy = h / 2;
    ctx.shadowColor = "rgba(0,0,0,0.25)";
    ctx.shadowBlur = Math.max(0, Math.floor(px * 0.15));
    if (outlineWidth > 0) {
      ctx.lineWidth = outlineWidth;
      ctx.strokeStyle = outline;
      ctx.strokeText(text || "", cx, cy);
    }
    ctx.fillStyle = fill;
    ctx.fillText(text || "", cx, cy);
    return canvas;
  };

  const computeCurrentZoom = () => {
    const scene = viewer.scene;
    const canvas = scene.canvas;
    const centerWin = new Cesium.Cartesian2(canvas.clientWidth / 2, canvas.clientHeight / 2);
    const ellipsoid = scene.globe.ellipsoid;
    let lat = 0;
    let mpp = null;

    const centerCart = scene.camera.pickEllipsoid(centerWin, ellipsoid);
    if (centerCart) {
      const p1 = Cesium.SceneTransforms?.wgs84ToWindowCoordinates ? Cesium.SceneTransforms.wgs84ToWindowCoordinates(scene, centerCart) : viewer.scene.cartesianToCanvasCoordinates(centerCart);

      const enu = Cesium.Transforms.eastNorthUpToFixedFrame(centerCart);
      const p2World = Cesium.Matrix4.multiplyByPoint(enu, new Cesium.Cartesian3(1, 0, 0), new Cesium.Cartesian3()); // +1m east
      const p2 = Cesium.SceneTransforms?.wgs84ToWindowCoordinates ? Cesium.SceneTransforms.wgs84ToWindowCoordinates(scene, p2World) : viewer.scene.cartesianToCanvasCoordinates(p2World);

      if (p1 && p2) {
        const dpx = Math.hypot(p2.x - p1.x, p2.y - p1.y);
        if (dpx > 0) mpp = 1 / dpx;
      }
      lat = Cesium.Cartographic.fromCartesian(centerCart).latitude;
    }

    if (mpp == null) {
      const carto = scene.camera.positionCartographic;
      const height = carto.height;
      const fovy = scene.camera.frustum.fovy;
      const metersScreenH = 2 * height * Math.tan(fovy / 2);
      mpp = metersScreenH / canvas.height;
      lat = carto.latitude || 0;
    }

    const EQUATOR_MPP_Z0 = 156543.03392804097;
    const z = Math.log2((EQUATOR_MPP_Z0 * Math.cos(lat)) / mpp);
    const zr = Number.isFinite(z) ? Math.round(z * 100) / 100 : 0; // 2 decimals
    return zr;
  };

  // --- data source ---
  const dsName = layerName || `overlay-text-${id || ""}`;
  const dataSource = new Cesium.CustomDataSource(dsName);
  dataSource.show = !invisible;

  // styles
  const styleFillColor = options.defaultFillColor || "#ffffff";
  const styleOutlineColor = options.defaultOutlineColor || "#000000";
  const styleStrokeWidth = options.defaultLineWidth || 1;
  const styleType = options.defaultStyle || "solid";

  // features
  const features = geojson?.type === "FeatureCollection" ? geojson.features || [] : geojson?.type === "Feature" ? [geojson] : [];

  // union bounds
  let union = newEmptyBounds();

  // entities
  const states = [];
  dataSource._overlayTextStates = states;
  for (const feat of features) {
    const props = feat.properties || {};
    const [lon, lat] = featureCenterLL(feat);
    const b = featureBounds(feat);
    addCoord(union, b.w, b.s);
    addCoord(union, b.e, b.n);

    const text = String(props.text ?? "");
    const minZoom = Number.isFinite(+props.zoom) ? Math.round(+props.zoom * 100) / 100 : 0;
    const baseSize = Number.isFinite(+props.size) ? Math.max(1, +props.size) : 12;
    const rotationRad = toRadians(Number.isFinite(+props.rotationDeg) ? +props.rotationDeg : 0);

    const baseCanvas = makeTextCanvas(text, baseSize, styleFillColor, styleOutlineColor, 3);

    const entity = dataSource.entities.add({
      id: `${dsName}-${dataSource.entities.values.length + 1}`,
      position: Cesium.Cartesian3.fromDegrees(lon, lat, 0),
      billboard: new Cesium.BillboardGraphics({
        image: baseCanvas,
        rotation: rotationRad,
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        verticalOrigin: Cesium.VerticalOrigin.CENTER,
        scale: 1,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        pixelOffset: new Cesium.Cartesian2(0, 0),
      }),
      show: false,
    });

    const st = { entity, minZoom, baseSize };
    states.push(st);
    entity._otState = st; // state used by applyZoom
    entity._otFeature = feat;
  }

  await viewer.dataSources.add(dataSource);

  // JS - enable click-to-edit for this dataSource
  const clickHandler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

  clickHandler.setInputAction((movement) => {
    const picked = viewer.scene.pick(movement.position);
    if (!picked || !picked.id || !dataSource.entities.contains(picked.id)) return;

    const entity = picked.id;
    const feat = entity._otFeature; // set in step 1
    const st = entity._otState; // set in step 1
    if (!feat || !st) return;

    openOverlayTextEditor({ viewer, dataSource, entity, feature: feat, stateRef: st, setMapObjects, originalCameraControls });
  }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

  // keep reference to clean up on layer removal
  dataSource._overlayTextClickHandler = clickHandler;

  // zoom to union on load (enabled by default; set options.zoomToDataSource === false to skip)
  const shouldZoom = options.zoomToDataSource !== false;
  if (shouldZoom && Number.isFinite(union.w) && Number.isFinite(union.s) && Number.isFinite(union.e) && Number.isFinite(union.n)) {
    // pad a bit
    const padLon = Math.max(0.0005, (union.e - union.w) * 0.05);
    const padLat = Math.max(0.0005, (union.n - union.s) * 0.05);
    const rect = Cesium.Rectangle.fromDegrees(Math.max(-180, union.w - padLon), Math.max(-90, union.s - padLat), Math.min(180, union.e + padLon), Math.min(90, union.n + padLat));
    viewer.camera.flyTo({ destination: rect, duration: 0.8 });
  }

  const stepZoom = (z) => z; // no snapping

  const applyZoom = (z) => {
    const zi = stepZoom(z); // already 2 decimals from computeCurrentZoom
    for (const s of states) {
      const visible = zi >= s.minZoom;
      s.entity.show = visible && dataSource.show;
      if (visible) {
        const desired = s.baseSize + Math.max(0, zi - s.minZoom);
        s.entity.billboard.scale = desired / s.baseSize;
      }
    }
  };

  applyZoom(computeCurrentZoom());
  viewer.scene.requestRender();

  // updater
  const updater = () => {
    if (!viewer.dataSources.contains(dataSource)) {
      viewer.scene.postRender.removeEventListener(updater);
      if (dataSource._overlayTextClickHandler) {
        dataSource._overlayTextClickHandler.destroy();
        dataSource._overlayTextClickHandler = null;
      }
      return;
    }
    applyZoom(computeCurrentZoom());
  };
  viewer.scene.postRender.addEventListener(updater);

  // callback
  if (typeof callback === "function") {
    const billboards = dataSource.entities.values;
    callback(dataSource, geojson, billboards, styleFillColor, styleOutlineColor, styleStrokeWidth, styleType);
  }

  dataSource._overlayTextUpdater = updater;
  return dataSource;
}

// Add one OverlayText feature to an existing dataSource and keep it reactive to zoom.
// No changes to your zoom computation block.
export function addOverlayTextFeatureToDataSource({ viewer, dataSource, feature, style = { fill: "#ffffff", outline: "#000000", outlineWidth: 3 } }) {
  // --- helpers ---
  const toRad = (d) => (d * Math.PI) / 180;
  const featureCenterLL = (feat) => {
    const p = feat.properties || {};
    if (p.center && Number.isFinite(p.center.lon) && Number.isFinite(p.center.lat)) return [p.center.lon, p.center.lat];
    if (Array.isArray(p.bbox) && p.bbox.length === 4) {
      const [w, s, e, n] = p.bbox;
      return [(w + e) / 2, (s + n) / 2];
    }
    const g = feat.geometry || {};
    if (g?.type === "Point" && Array.isArray(g.coordinates)) return [g.coordinates[0], g.coordinates[1]];
    return [0, 0];
  };
  const makeTextCanvas = (text, px, fill, outline, outlineWidth) => {
    const dpr = window.devicePixelRatio || 1;
    const pad = Math.max(4, Math.ceil(px * 0.3));
    const tmp = document.createElement("canvas");
    const tctx = tmp.getContext("2d");
    tctx.font = `${px}px sans-serif`;
    const m = tctx.measureText(text || "");
    const baseH = Math.ceil(px * 1.2);
    const baseW = Math.ceil((m.actualBoundingBoxRight || m.width) - (m.actualBoundingBoxLeft || 0));
    const w = Math.max(1, baseW + pad * 2),
      h = Math.max(1, baseH + pad * 2);

    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(w * dpr);
    canvas.height = Math.ceil(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;

    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.font = `${px}px sans-serif`;
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    const cx = w / 2,
      cy = h / 2;
    ctx.shadowColor = "rgba(0,0,0,0.25)";
    ctx.shadowBlur = Math.max(0, Math.floor(px * 0.15));
    if (outlineWidth > 0) {
      ctx.lineWidth = outlineWidth;
      ctx.strokeStyle = outline;
      ctx.strokeText(text || "", cx, cy);
    }
    ctx.fillStyle = fill;
    ctx.fillText(text || "", cx, cy);
    return canvas;
  };

  const computeCurrentZoom = () => {
    const scene = viewer.scene;
    const canvas = scene.canvas;
    const centerWin = new Cesium.Cartesian2(canvas.clientWidth / 2, canvas.clientHeight / 2);
    const ellipsoid = scene.globe.ellipsoid;

    let centerCart = scene.camera.pickEllipsoid(centerWin, ellipsoid);
    let lat = 0;
    let mpp = null;

    if (centerCart) {
      const p1 = Cesium.SceneTransforms?.wgs84ToWindowCoordinates ? Cesium.SceneTransforms.wgs84ToWindowCoordinates(scene, centerCart) : viewer.scene.cartesianToCanvasCoordinates(centerCart);
      const enu = Cesium.Transforms.eastNorthUpToFixedFrame(centerCart);
      const p2World = Cesium.Matrix4.multiplyByPoint(enu, new Cesium.Cartesian3(1, 0, 0), new Cesium.Cartesian3()); // +1m east
      const p2 = Cesium.SceneTransforms?.wgs84ToWindowCoordinates ? Cesium.SceneTransforms.wgs84ToWindowCoordinates(scene, p2World) : viewer.scene.cartesianToCanvasCoordinates(p2World);

      if (p1 && p2) {
        const dpx = Math.hypot(p2.x - p1.x, p2.y - p1.y);
        if (dpx > 0) mpp = 1 / dpx;
      }
      lat = Cesium.Cartographic.fromCartesian(centerCart).latitude;
    }
    if (mpp == null) {
      const carto = scene.camera.positionCartographic;
      const height = carto.height;
      const fovy = scene.camera.frustum.fovy;
      const metersScreenH = 2 * height * Math.tan(fovy / 2);
      mpp = metersScreenH / canvas.height;
      lat = carto.latitude || 0;
    }
    const EQUATOR_MPP_Z0 = 156543.03392804097; // 2πR/256
    const z = Math.log2((EQUATOR_MPP_Z0 * Math.cos(lat)) / mpp);
    return Number.isFinite(z) ? z : 0;
  };
  // ---------------------------------------------

  const stepZoom = (z) => z;

  const props = feature.properties || {};
  const [lon, lat] = featureCenterLL(feature);
  const text = String(props.text ?? "");
  const minZoom = Number.isFinite(+props.zoom) ? Math.round(+props.zoom * 100) / 100 : 0;
  const baseSize = Number.isFinite(+props.size) ? Math.max(1, +props.size) : 12;
  const rotationRad = toRad(Number.isFinite(+props.rotationDeg) ? +props.rotationDeg : 0);

  const image = makeTextCanvas(text, baseSize, style.fill, style.outline, style.outlineWidth);

  const entity = dataSource.entities.add({
    id: `${dataSource.name || "overlay-text"}-${dataSource.entities.values.length + 1}`,
    position: Cesium.Cartesian3.fromDegrees(lon, lat, 0),

    show: true,
    billboard: new Cesium.BillboardGraphics({
      image,
      rotation: rotationRad,
      horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
      verticalOrigin: Cesium.VerticalOrigin.CENTER,

      scale: new Cesium.CallbackProperty(() => {
        const z = computeCurrentZoom();
        if (z < minZoom) return 1;
        const desired = baseSize + (z - minZoom);
        return desired / baseSize;
      }, false),

      show: new Cesium.CallbackProperty(() => {
        const z = computeCurrentZoom();
        return dataSource.show && z >= minZoom;
      }, false),

      disableDepthTestDistance: Number.POSITIVE_INFINITY,
      pixelOffset: new Cesium.Cartesian2(0, 0),
    }),
  });

  // attach feature + state for editor
  const st = { entity, minZoom, baseSize };
  entity._otFeature = feature;
  entity._otState = st;

  // if loader has a global states list, register this one too
  if (Array.isArray(dataSource._overlayTextStates)) {
    dataSource._overlayTextStates.push(st);
  }

  viewer.scene.requestRender();
  return entity;
}
